import { ChevronDown } from "lucide-react";
import { Reveal } from "@/components/shared/Reveal";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { ConstellationField, CelestialGlyphRow } from "@/components/shared/Motifs";
import { useLanguage } from "@/context/LanguageContext";
import { CONTENT } from "@/i18n/content";

const GLYPHS = ["\u2644", "\u2643", "\u263D", "\u2609"];

export function WhatPowers() {
  const { lang } = useLanguage();
  const t = CONTENT[lang].powers;

  return (
    <section id="powers" className="relative scroll-mt-20 overflow-hidden bg-cream py-20 sm:py-28" data-testid="what-powers">
      <ConstellationField className="pointer-events-none absolute right-0 top-0 h-[420px] w-[420px] text-navy/[0.05]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading eyebrow={t.eyebrow} title={t.title} sub={t.sub} />
        <Reveal delay={80} className="mt-8">
          <CelestialGlyphRow className="text-gold/70" />
          <p className="mt-3 text-center text-xs font-medium uppercase tracking-[0.2em] text-navy/45">
            {lang === "hi" ? "\u0928\u0935\u0917\u094d\u0930\u0939 \u2022 \u0935\u0948\u0926\u093f\u0915 \u091c\u094d\u092f\u094b\u0924\u093f\u0937" : "Navagraha \u2022 Vedic Astrology"}
          </p>
        </Reveal>

        {/* Flow */}
        <div className="mx-auto mt-14 max-w-2xl">
          <div className="flex flex-col items-stretch gap-2">
            {t.flow.map((step, i) => (
              <Reveal key={step} delay={i * 80}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-full rounded-2xl px-6 py-4 text-center text-sm font-medium shadow-sm sm:text-base ${
                      i === t.flow.length - 1
                        ? "bg-gold text-navy"
                        : i === 0
                        ? "bg-navy text-cream"
                        : "border border-navy/10 bg-white text-navy"
                    }`}
                  >
                    {step}
                  </div>
                  {i < t.flow.length - 1 && (
                    <ChevronDown className="my-1 h-5 w-5 text-gold" strokeWidth={2.5} />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Principles */}
        <div className="mt-16 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {t.principles.map((p, i) => (
            <Reveal key={p.title} delay={(i % 4) * 80}>
              <div className="h-full rounded-3xl border border-navy/8 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_36px_-16px_rgba(15,27,61,0.18)]">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-navy font-heading text-xl text-gold">{GLYPHS[i]}</span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{p.title}</h3>
                <div className="mt-2 h-px w-8 bg-gold" />
                <p className="mt-3 text-sm leading-relaxed text-navy/65">{p.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
